import { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Stack, Typography } from "@mui/material";
import { Navigate, useNavigate, useParams } from "react-router-dom";

import StockPageHeader from "../../../components/StockPageHeader";
import {
  getPreparedProduct,
  getPreparedProductRecipe,
  getPreparedProductRecipes,
} from "../api/preparedProductsApi";
import { parsePreparedProductRecipeNotes } from "../utils/preparedProductRecipeNotes";
import { formatPreparedProductSectors } from "../utils/preparedProductSector.utils";

const BASE_UNIT_LABELS = {
  g: "Gramos (g)",
  ml: "Mililitros (ml)",
  unit: "Unidad",
  package: "Paquete",
};

function unwrapHttp(res) {
  if (res && typeof res === "object" && "data" in res && !("meta" in res)) {
    return res.data;
  }
  return res;
}

function listFromPaginated(data) {
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data)) return data;
  return [];
}

function errorToMessage(err) {
  return (
    err?.response?.data?.detail ||
    err?.response?.data?.message ||
    err?.data?.message ||
    err?.message ||
    "Error al cargar la receta."
  );
}

function formatYield(value) {
  if (value === null || value === undefined || value === "") return "-";

  const numberValue = Number(value);
  if (!Number.isFinite(numberValue)) return String(value);

  return `${(numberValue * 100).toLocaleString("es-AR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 4,
  })}%`;
}

function formatNumber(value) {
  if (value === null || value === undefined || value === "") return "-";

  const numberValue = Number(value);
  if (!Number.isFinite(numberValue)) return String(value);

  return numberValue.toLocaleString("es-AR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 4,
  });
}

function formatQuantity(value, unit) {
  const formatted = formatNumber(value);
  if (formatted === "-") return formatted;
  return unit ? `${formatted} ${unit}` : formatted;
}

function unitLabel(unit) {
  return BASE_UNIT_LABELS[unit] || unit || "-";
}

function CardShell({ children, sx }) {
  return (
    <Box
      sx={{
        p: { xs: 2, lg: 3 },
        bgcolor: "neutral.300",
        borderRadius: "30px",
        boxShadow: "inset 0px 0px 4px rgba(117, 117, 138, 0.5)",
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

function SectionTitle({ children, helper }) {
  return (
    <Box>
      <Typography sx={{ fontSize: 17, fontWeight: 800, color: "primary.900" }}>
        {children}
      </Typography>
      {helper && (
        <Typography sx={{ mt: 0.25, fontSize: 12, fontWeight: 600, color: "neutral.700" }}>
          {helper}
        </Typography>
      )}
    </Box>
  );
}

function InfoRow({ label, value }) {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "130px 1fr" },
        gap: { xs: 0.4, sm: 1.2 },
        alignItems: "center",
      }}
    >
      <Typography sx={{ fontSize: 12, fontWeight: 800, color: "neutral.700" }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 14, fontWeight: 800, color: "primary.900" }}>
        {value || "-"}
      </Typography>
    </Box>
  );
}

function StatusPill({ active }) {
  return (
    <Box
      sx={{
        width: 84,
        px: 1,
        py: 0.55,
        borderRadius: "20px",
        bgcolor: active ? "rgba(33, 150, 83, 0.16)" : "rgba(195, 60, 60, 0.16)",
        boxShadow: "inset 0px 0px 3px rgba(142, 142, 159, 0.28)",
      }}
    >
      <Typography
        sx={{
          color: active ? "rgba(18, 111, 63, 1)" : "rgba(160, 49, 49, 1)",
          fontSize: 12,
          fontWeight: 800,
          textAlign: "center",
        }}
      >
        {active ? "Activo" : "Inactivo"}
      </Typography>
    </Box>
  );
}

function ComponentLine({ name, quantity, notes, divider = true }) {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "minmax(0, 1fr) 112px" },
        gap: 1,
        alignItems: "start",
        py: 1,
        borderBottom: divider ? "1px solid rgba(0,0,0,.07)" : "none",
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography
          noWrap
          title={name}
          sx={{ fontSize: 14, fontWeight: 800, color: "primary.900" }}
        >
          {name}
        </Typography>
        {notes && (
          <Typography sx={{ mt: 0.25, fontSize: 12, fontWeight: 600, color: "neutral.700" }}>
            {notes}
          </Typography>
        )}
      </Box>
      <Typography
        sx={{
          justifySelf: { xs: "start", sm: "end" },
          minWidth: 86,
          px: 1.2,
          py: 0.45,
          borderRadius: "16px",
          bgcolor: "secondary.100",
          color: "primary.900",
          fontSize: 12,
          fontWeight: 800,
          textAlign: "center",
        }}
      >
        {quantity}
      </Typography>
    </Box>
  );
}

function EmptyState({ children }) {
  return (
    <Typography sx={{ py: 1.2, fontSize: 13, fontWeight: 700, color: "neutral.700" }}>
      {children}
    </Typography>
  );
}

export default function PreparedProductRecipeDetailPage() {
  const navigate = useNavigate();
  const { preparedProductId } = useParams();

  const [preparedProduct, setPreparedProduct] = useState(null);
  const [recipe, setRecipe] = useState(null);
  const [ingredientLines, setIngredientLines] = useState([]);
  const [preparedProductLines, setPreparedProductLines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const numericPreparedProductId = Number(preparedProductId);

  const fetchRecipeDetail = useCallback(async (signal) => {
    setLoading(true);
    setError("");

    try {
      const product = unwrapHttp(
        await getPreparedProduct(numericPreparedProductId, { signal }),
      );
      const recipesData = unwrapHttp(
        await getPreparedProductRecipes(numericPreparedProductId, {
          page: 1,
          limit: 1,
          signal,
        }),
      );
      if (signal.aborted) return;
      const firstRecipe = listFromPaginated(recipesData)[0] || null;

      setPreparedProduct(product);

      if (!firstRecipe?.id) {
        setRecipe(null);
        setIngredientLines([]);
        setPreparedProductLines([]);
        return;
      }

      const fullRecipe = unwrapHttp(
        await getPreparedProductRecipe(numericPreparedProductId, firstRecipe.id, { signal })
      );
      if (signal.aborted) return;

      const ingredients = Array.isArray(fullRecipe?.ingredients)
        ? fullRecipe.ingredients
        : [];
      const components = Array.isArray(fullRecipe?.preparedProducts)
        ? fullRecipe.preparedProducts
        : [];

      const hydratedIngredients = ingredients.map((line) => ({
        ...line,
        name: line.ingredient?.name || `Insumo #${line.ingredientId}`,
        unit: line.ingredient?.baseUnit || "",
      }));

      const hydratedComponents = components.map((line) => ({
        ...line,
        name:
          line.componentPreparedProduct?.name ||
          `Elaborado #${line.componentPreparedProductId}`,
        unit: line.componentPreparedProduct?.resultUnit || "",
      }));

      setRecipe(fullRecipe);
      setIngredientLines(hydratedIngredients);
      setPreparedProductLines(hydratedComponents);
    } catch (err) {
      if (signal.aborted) return;
      setPreparedProduct(null);
      setRecipe(null);
      setIngredientLines([]);
      setPreparedProductLines([]);
      setError(errorToMessage(err));
    } finally {
      if (!signal.aborted) setLoading(false);
    }
  }, [numericPreparedProductId]);

  useEffect(() => {
    if (Number.isFinite(numericPreparedProductId) && numericPreparedProductId > 0) {
      const controller = new AbortController();
      fetchRecipeDetail(controller.signal);
      return () => controller.abort();
    }
    return undefined;
  }, [fetchRecipeDetail, numericPreparedProductId]);

  const title = preparedProduct?.name || "Receta elaborado";

  const recipeSummary = useMemo(
    () => [
      ["Cantidad generada", formatQuantity(recipe?.resultQuantity, preparedProduct?.resultUnit)],
      ["Rendimiento", formatYield(preparedProduct?.yieldFactor)],
    ],
    [preparedProduct?.resultUnit, preparedProduct?.yieldFactor, recipe]
  );
  const recipePreparation = useMemo(
    () => parsePreparedProductRecipeNotes(recipe?.notes),
    [recipe?.notes],
  );

  if (!Number.isFinite(numericPreparedProductId) || numericPreparedProductId <= 0) {
    return <Navigate to="/stock/base-de-datos/elaborados" replace />;
  }

  return (
    <Stack spacing={2} sx={{ p: 2 }}>
      <StockPageHeader
        title={title}
        onBack={() => navigate("/stock/base-de-datos/elaborados")}
      />

      <CardShell>
        {!!error && (
          <Box
            sx={{
              mb: 1.5,
              px: 2,
              py: 1.2,
              borderRadius: "18px",
              bgcolor: "secondary.700",
              boxShadow: "inset 0px 0px 2px rgba(96, 96, 113, 0.35)",
            }}
          >
            <Typography sx={{ fontSize: 13, fontWeight: 800, color: "logoblanco" }}>
              No se pudo cargar la receta
            </Typography>
            <Typography
              sx={{ fontSize: 12, fontWeight: 600, color: "logoblanco", opacity: 0.95 }}
            >
              {error}
            </Typography>
          </Box>
        )}

        <Box
          sx={{
            bgcolor: "neutral.50",
            borderRadius: "22px",
            px: { xs: 2, lg: 3 },
            py: { xs: 2, lg: 2.5 },
            minHeight: 470,
          }}
        >
          {loading ? (
            <Typography sx={{ fontSize: 14, fontWeight: 700, color: "neutral.700" }}>
              Cargando receta...
            </Typography>
          ) : (
            <Stack spacing={2.4}>
              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={1.2}
                sx={{
                  alignItems: { xs: "flex-start", md: "center" },
                  justifyContent: "space-between",
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontSize: 13,
                      fontWeight: 800,
                      color: "primary.900",
                      textTransform: "uppercase",
                    }}
                  >
                    Vista de receta
                  </Typography>
                </Box>
                <StatusPill active={preparedProduct?.isActive} />
              </Stack>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", lg: "0.9fr 1.35fr" },
                  gap: { xs: 2.5, lg: 3 },
                  alignItems: "stretch",
                }}
              >
                <Stack spacing={2}>
                  <SectionTitle>
                    Información general
                  </SectionTitle>

                  <Stack spacing={1.3}>
                    <InfoRow label="Nombre" value={preparedProduct?.name} />
                    <InfoRow
                      label="Sector"
                      value={formatPreparedProductSectors(preparedProduct?.sector)}
                    />
                    <InfoRow label="Unidad" value={unitLabel(preparedProduct?.resultUnit)} />
                    <InfoRow label="Rendimiento" value={formatYield(preparedProduct?.yieldFactor)} />
                  </Stack>

                  <Box sx={{ pt: 1 }}>
                    <SectionTitle>
                      Receta elaborado
                    </SectionTitle>
                  </Box>

                  {recipe ? (
                    <Stack spacing={1.2}>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 1.2,
                        }}
                      >
                        {recipeSummary.map(([label, value]) => (
                          <Box
                            key={label}
                            sx={{
                              px: 1.4,
                              py: 1,
                              borderRadius: "16px",
                              bgcolor: "secondary.50",
                            }}
                          >
                            <Typography sx={{ fontSize: 11.5, fontWeight: 800, color: "neutral.700" }}>
                              {label}
                            </Typography>
                            <Typography sx={{ mt: 0.25, fontSize: 14, fontWeight: 900, color: "primary.900" }}>
                              {value}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </Stack>
                  ) : (
                    <EmptyState>Este elaborado todavía no tiene una receta cargada.</EmptyState>
                  )}
                </Stack>

                <Stack
                  spacing={1.5}
                  sx={{
                    minHeight: 0,
                    height: { xs: "auto", lg: 460 },
                    pl: { xs: 0, lg: 3 },
                    borderLeft: { xs: "none", lg: "2px solid rgba(2, 8, 23, 0.45)" },
                  }}
                >
                  <Box
                    sx={{
                      minHeight: 0,
                      flex: { xs: "none", lg: 1 },
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <SectionTitle helper="Insumos y elaborados que componen la receta">
                      Lista de ingredientes
                    </SectionTitle>

                    <Box
                      sx={{
                        mt: 1,
                        minHeight: 0,
                        maxHeight: { xs: 260, lg: "none" },
                        overflowY: "auto",
                        pr: 0.8,
                      }}
                    >
                      <Stack spacing={1}>
                        {ingredientLines.length ? (
                          <Box>
                            <Typography sx={{ mb: 0.5, fontSize: 14, fontWeight: 900, color: "primary.900" }}>
                              Insumos
                            </Typography>
                            {ingredientLines.map((line, index) => (
                            <ComponentLine
                              key={line.id}
                              name={line.name}
                              quantity={formatQuantity(line.quantity, line.unit)}
                              notes={line.notes}
                              divider={index < ingredientLines.length - 1 || preparedProductLines.length > 0}
                            />
                            ))}
                          </Box>
                        ) : null}
                        {preparedProductLines.length ? (
                          <Box>
                            <Typography sx={{ mb: 0.5, pt: ingredientLines.length ? 0.8 : 0, fontSize: 14, fontWeight: 900, color: "primary.900" }}>
                              Elaborados
                            </Typography>
                            {preparedProductLines.map((line, index) => (
                            <ComponentLine
                              key={line.id}
                              name={line.name}
                              quantity={formatQuantity(line.quantity, line.unit)}
                              notes={line.notes}
                              divider={index < preparedProductLines.length - 1}
                            />
                            ))}
                          </Box>
                        ) : null}
                        {!ingredientLines.length && !preparedProductLines.length ? (
                          <EmptyState>La receta no tiene componentes cargados.</EmptyState>
                        ) : null}
                      </Stack>
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      minHeight: 0,
                      flex: { xs: "none", lg: 1 },
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <SectionTitle>Preparación</SectionTitle>
                    <Box
                      sx={{
                        mt: 1,
                        minHeight: { xs: 130, lg: 0 },
                        maxHeight: { xs: 260, lg: "none" },
                        flex: { xs: "none", lg: 1 },
                        overflowY: "auto",
                        px: 1.4,
                        py: 1.2,
                        borderRadius: "16px",
                        bgcolor: "neutral.100",
                      }}
                    >
                      {recipePreparation.description || recipePreparation.steps.length ? (
                        <Stack spacing={1.2}>
                          {recipePreparation.description ? (
                            <Box>
                              <Typography sx={{ mb: 0.35, fontSize: 12, fontWeight: 900, color: "primary.900" }}>
                                Descripción
                              </Typography>
                              <Typography sx={{ whiteSpace: "pre-line", fontSize: 13, fontWeight: 650, color: "neutral.800", overflowWrap: "anywhere" }}>
                                {recipePreparation.description}
                              </Typography>
                            </Box>
                          ) : null}
                          {recipePreparation.steps.length ? (
                            <Box>
                              <Typography sx={{ mb: 0.35, fontSize: 12, fontWeight: 900, color: "primary.900" }}>
                                Pasos
                              </Typography>
                              <Box component="ol" sx={{ my: 0, pl: 2.5 }}>
                                {recipePreparation.steps.map((step, index) => (
                                  <Typography component="li" key={`${step}-${index}`} sx={{ mb: 0.45, pl: 0.4, fontSize: 13, fontWeight: 650, color: "neutral.800" }}>
                                    {step}
                                  </Typography>
                                ))}
                              </Box>
                            </Box>
                          ) : null}
                        </Stack>
                      ) : (
                        <Typography sx={{ fontSize: 13, fontWeight: 650, color: "neutral.800" }}>
                          Sin descripción ni pasos de preparación.
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Stack>
              </Box>
            </Stack>
          )}
        </Box>
      </CardShell>
    </Stack>
  );
}
